import { z } from 'zod';
import { entityIdSchema, localeSchema, localizedTextSchema } from './common';
import { objectiveIdSchema } from './mission';

/**
 * Challenge authoring manifest (Phase 6, ADR 0012). A mission names a manifest by
 * `gameManifestId` + `manifestSchemaVersion`; the manifest holds the circuit challenges: the
 * starting board, the goal as a small truth table, hints and a reference solution that the tests
 * prove works (and that the starting board does not already satisfy). Texts are authored content
 * in en (required) plus et/fi/de, resolved like programme text.
 */

export const CIRCUIT_PART_KINDS = ['battery', 'lamp', 'switch', 'light-sensor'] as const;
export const BOARD_COLUMNS = 12;
export const BOARD_ROWS = 8;

const terminalSchema = z.strictObject({ part: entityIdSchema, side: z.enum(['a', 'b']) });

const boardPartSchema = z.strictObject({
  id: entityIdSchema,
  kind: z.enum(CIRCUIT_PART_KINDS),
  /** Grid cell of the part's centre; terminals sit on its left (a) and right (b). */
  x: z
    .int()
    .min(1)
    .max(BOARD_COLUMNS - 2),
  y: z
    .int()
    .min(0)
    .max(BOARD_ROWS - 1),
  closed: z.boolean().optional(),
  broken: z.boolean().optional(),
});

const boardWireSchema = z.strictObject({
  id: entityIdSchema,
  from: terminalSchema,
  to: terminalSchema,
  broken: z.boolean().optional(),
  /** Locked wires belong to the scenery and cannot be removed. */
  locked: z.boolean().optional(),
});

const goalRowSchema = z.strictObject({
  when: z.strictObject({
    switches: z.record(entityIdSchema, z.boolean()).optional(),
    dark: z.boolean().optional(),
  }),
  expect: z.record(entityIdSchema, z.enum(['on', 'off'])),
});

const solutionSchema = z.strictObject({
  removeWires: z.array(entityIdSchema).default([]),
  replaceLamps: z.array(entityIdSchema).default([]),
  addWires: z.array(z.strictObject({ from: terminalSchema, to: terminalSchema })).default([]),
});

export const circuitChallengeSchema = z.strictObject({
  id: entityIdSchema,
  version: z.int().min(1),
  objectiveId: objectiveIdSchema,
  /** 1 = first step, 2 = harder; "try easier/harder" moves between levels. */
  level: z.int().min(1).max(3),
  title: localizedTextSchema(80),
  story: localizedTextSchema(400),
  goal: localizedTextSchema(200),
  hints: z.array(localizedTextSchema(300)).min(1).max(3),
  success: localizedTextSchema(300),
  board: z.strictObject({
    parts: z.array(boardPartSchema).min(2).max(12),
    wires: z.array(boardWireSchema).max(20),
  }),
  tools: z.strictObject({ tester: z.boolean(), daylight: z.boolean() }),
  /** How many wires the child may add (the parts box). */
  maxNewWires: z.int().min(0).max(12),
  goalRows: z.array(goalRowSchema).min(1).max(8),
  solution: solutionSchema,
});
export type CircuitChallenge = z.output<typeof circuitChallengeSchema>;

export const circuitManifestSchema = z
  .strictObject({
    manifestId: entityIdSchema,
    schemaVersion: z.literal(1),
    challenges: z.array(circuitChallengeSchema).min(1).max(20),
  })
  .superRefine((manifest, ctx) => {
    const ids = new Set<string>();
    manifest.challenges.forEach((challenge, index) => {
      const at = ['challenges', index];
      if (ids.has(challenge.id))
        ctx.addIssue({ code: 'custom', path: at, message: 'Duplicate challenge id' });
      ids.add(challenge.id);
      const parts = new Map(challenge.board.parts.map((p) => [p.id, p]));
      if (parts.size !== challenge.board.parts.length) {
        ctx.addIssue({ code: 'custom', path: [...at, 'board'], message: 'Duplicate part id' });
      }
      const cells = new Set(challenge.board.parts.map((p) => `${p.x},${p.y}`));
      if (cells.size !== challenge.board.parts.length) {
        ctx.addIssue({ code: 'custom', path: [...at, 'board'], message: 'Two parts share a cell' });
      }
      const terminals = [
        ...challenge.board.wires.flatMap((w) => [w.from, w.to]),
        ...challenge.solution.addWires.flatMap((w) => [w.from, w.to]),
      ];
      if (terminals.some((t) => !parts.has(t.part))) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'board'],
          message: 'Wire to an unknown part',
        });
      }
      const named = challenge.goalRows.flatMap((r) => [
        ...Object.keys(r.expect),
        ...Object.keys(r.when.switches ?? {}),
      ]);
      if (named.some((id) => !parts.has(id))) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'goalRows'],
          message: 'Goal names an unknown part',
        });
      }
      if (challenge.solution.addWires.length > challenge.maxNewWires) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'solution'],
          message: 'Solution needs more wires than allowed',
        });
      }
    });
  });
export type CircuitManifest = z.output<typeof circuitManifestSchema>;

/** Public, localised mission summary for guests (`GET /api/public/missions`). */
export const publicMissionSchema = z.strictObject({
  id: entityIdSchema,
  gameManifestId: entityIdSchema,
  manifestSchemaVersion: z.int().min(1),
  objectiveIds: z.array(objectiveIdSchema),
  ageMin: z.int(),
  ageMax: z.int(),
  title: z.string(),
  instructions: z.string(),
  textLocale: localeSchema,
});
export type PublicMission = z.infer<typeof publicMissionSchema>;

export const publicMissionListSchema = z.strictObject({ missions: z.array(publicMissionSchema) });
export type PublicMissionList = z.infer<typeof publicMissionListSchema>;

export const publicMissionQuerySchema = z.strictObject({ locale: localeSchema });
export const publicMissionParamsSchema = z.strictObject({ id: entityIdSchema });
